import axios from 'axios';
import { toast } from 'sonner';
import { safePrintWindow, escapeHtml } from '../../utils/printUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const r = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '');

const styles = `body{font-family:Arial,sans-serif;color:#000;margin:0;padding:16px;font-size:12px;line-height:1.3}
  h1{font-size:22px;margin:0;font-weight:900;text-align:center}.sub{text-align:center;font-size:12px}
  .title{text-align:center;font-size:15px;font-weight:900;margin:8px 0 4px;border-top:2px solid #000;border-bottom:2px solid #000;padding:4px}
  .status{text-align:center;font-weight:800;margin-bottom:8px}
  h2{font-size:13px;margin:12px 0 4px;font-weight:900;text-transform:uppercase;border-bottom:1px solid #000}
  table{width:100%;border-collapse:collapse}th,td{border:1px solid #000;padding:3px 6px}
  th{background:#e5e7eb;font-weight:800;text-align:left}.n{text-align:right}.b{font-weight:800}
  .loss{color:#b91c1c;font-weight:800}.tot td{font-weight:900;background:#f3f4f6}
  .grid{display:flex;gap:10px}.grid>div{flex:1}
  .sig{display:flex;justify-content:space-between;margin-top:40px}.sig div{width:30%;border-top:1px solid #000;text-align:center;padding-top:4px;font-weight:700}
  .note{border:1px solid #000;padding:6px;min-height:24px}@page{size:A4;margin:10mm}`;

const row = (cells, cls = '') => `<tr class="${cls}">${cells.map((c, i) => `<td class="${i ? 'n' : ''}">${c}</td>`).join('')}</tr>`;

const buildHtml = (d) => {
  const s = d.saved || {};
  const doc = (s.locked && s.doctor_auto) || d.doctor_auto || {};
  const nur = (s.locked && s.nursing_auto) || d.nursing_auto || {};
  const ph = s.pharmacy_manual || {};
  const cash = s.cash_manual || {};
  const fin = s.final_override || {};
  const rl = d.reception_loss || {};
  const pl = d.pharmacy_loss;
  const f = d.fees;
  const services = Object.entries(nur.services || {});

  const consRows = [
    ['OP (Regular)', doc.op_count, f.regular_fee], ['ER', doc.er_count, f.er_fee], ['Review', doc.review_count, f.review_fee],
  ].map(([k, c, fee]) => row([k, c || 0, r(fee), r((c || 0) * fee)])).join('');

  const nurRows = services.map(([, v]) => row([escapeHtml(v.name), v.count, r(v.revenue)])).join('');

  let pharmRows = '';
  let nurLoss = 0;
  if (s.locked) {
    const cons = [['OP', 'op'], ['ER', 'er'], ['Review', 'review']].map(([label, k]) => {
      const x = pl?.[k] || { system: 0, pharmacy: 0, missed: 0, loss: 0 };
      return row([label, x.system, x.pharmacy, x.missed, `<span class="${x.loss ? 'loss' : ''}">${r(x.loss)}</span>`]);
    }).join('');
    const nurs = services.map(([k, v]) => {
      const p = (ph.nursing_services || {})[k] || 0;
      const missed = Math.max(0, v.count - p);
      const loss = missed * (v.count ? v.revenue / v.count : 0);
      nurLoss += loss;
      return row([escapeHtml(v.name), v.count, p, missed, `<span class="${loss ? 'loss' : ''}">${r(loss)}</span>`]);
    }).join('');
    pharmRows = cons + nurs;
  }
  const pharmLoss = (pl?.total_loss || 0) + nurLoss;
  const sysTotal = (doc.total_amount || 0) + (nur.total_amount || 0);
  const totalLoss = (rl.loss_amount || 0) + pharmLoss;

  const status = s.locked
    ? `SIGNED &amp; LOCKED by ${escapeHtml(s.signed_by)} &bull; ${fmtTime(s.submitted_at)}`
    : 'DRAFT &mdash; TALLY NOT YET SIGNED';

  return `<h1>K.M. MULTI SPECIALITY CLINIC</h1><div class="sub">K.M. சிறப்பு சிகிச்சை மையம், கோலியனூர் &bull; Clinic: 86085 95239</div>
  <div class="title">DAY END ACCOUNTS REPORT &mdash; ${fmtDate(d.date)}</div>
  <div class="status">${status}</div>

  <div class="grid"><div>
    <h2>1. Consultation (System)</h2>
    <table><tr><th>Type</th><th class="n">Count</th><th class="n">Fee</th><th class="n">Amount</th></tr>${consRows}
    ${row(['Exempted', doc.exempted_count || 0, '', ''])}
    ${row(['Total', doc.total_cases || 0, '', r(doc.total_amount)], 'tot')}</table>
  </div><div>
    <h2>2. Nursing Revenue (System)</h2>
    <table><tr><th>Service</th><th class="n">Count</th><th class="n">Amount</th></tr>${nurRows || row(['No services', '', ''])}
    ${row(['Total', nur.total_entries || 0, r(nur.total_amount)], 'tot')}
    ${nur.ecg_amount ? row(['(ECG incl.)', '', r(nur.ecg_amount)]) : ''}</table>
  </div></div>

  <h2>3. Pharmacy Reconciliation</h2>
  ${s.locked
    ? `<table><tr><th>Item</th><th class="n">System</th><th class="n">Pharmacy</th><th class="n">Missed</th><th class="n">Loss</th></tr>${pharmRows}
       ${row(['Pharmacy Loss', '', '', '', r(pharmLoss)], 'tot')}</table>`
    : '<div class="note">Pharmacy counts not entered &mdash; tally not signed yet.</div>'}

  <h2>4. Token Missed (Reception)</h2>
  <table><tr><th>Item</th><th class="n">Count</th><th class="n">Loss</th></tr>
    ${row(['Tokens issued', rl.tokens_issued || 0, ''])}${row(['Tokens consulted', rl.tokens_consulted || 0, ''])}
    ${row([`Not seen (OP ${rl.stranded_breakdown?.op || 0} / ER ${rl.stranded_breakdown?.er || 0} / Review ${rl.stranded_breakdown?.review || 0})`, rl.stranded || 0, r(rl.stranded_value)])}
    ${row(['Deleted tokens', rl.deleted || 0, r(rl.deleted_value)])}
    ${row(['Token Loss', rl.total_lost || 0, r(rl.loss_amount)], 'tot')}</table>

  <h2>5. Cash Count &amp; Final</h2>
  <table><tr><th>Head</th><th class="n">System</th><th class="n">Cash Counted</th><th class="n">Final (Signed)</th><th class="n">Difference</th></tr>
    ${row(['Consultation', r(doc.total_amount), s.locked ? r(cash.consultation_cash) : '-', s.locked ? r(fin.consultation_total) : '-', s.locked ? r((cash.consultation_cash || 0) - (doc.total_amount || 0)) : '-'])}
    ${row(['Nursing', r(nur.total_amount), s.locked ? r(cash.nursing_cash) : '-', s.locked ? r(fin.nursing_total) : '-', s.locked ? r((cash.nursing_cash || 0) - (nur.total_amount || 0)) : '-'])}
    ${row(['GRAND TOTAL', r(sysTotal), s.locked ? r((cash.consultation_cash || 0) + (cash.nursing_cash || 0)) : '-', s.locked ? r(fin.grand_total) : '-', ''], 'tot')}</table>
  ${s.discrepancy_note ? `<h2>Discrepancy Note</h2><div class="note">${escapeHtml(s.discrepancy_note)}</div>` : ''}

  <h2>Summary</h2>
  <table>${row(['System Amount (Consultation + Nursing)', `<b>${r(sysTotal)}</b>`])}
    ${row(['Final Amount (Signed)', s.locked ? `<b>${r(fin.grand_total)}</b>` : '-'])}
    ${row(['Pharmacy Loss', `<span class="loss">${s.locked ? r(pharmLoss) : '-'}</span>`])}
    ${row(['Token Missed Loss', `<span class="loss">${r(rl.loss_amount)}</span>`])}
    ${row(['TOTAL LOSS', `<span class="loss">${r(totalLoss)}</span>`], 'tot')}</table>

  <div class="sig"><div>Staff: ${escapeHtml(s.signed_by || '')}</div><div>Pharmacy</div><div>Doctor</div></div>
  <div style="text-align:center;font-size:10px;margin-top:10px">Printed ${fmtTime(new Date().toISOString())}</div>`;
};

export const printDayEndReport = async (token, dateStr) => {
  try {
    const { data } = await axios.get(`${API}/daily-tally/report`, {
      params: dateStr ? { date_str: dateStr } : {},
      headers: { Authorization: `Bearer ${token}` },
    });
    safePrintWindow(`Day End Report ${data.date}`, `<style>${styles}</style>${buildHtml(data)}`);
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Could not load day-end report');
  }
};
